import { SET_ASSIGNED_ARTWORK_ID } from '@app/actions/_types';
import { startLoading, endLoading, updateLoadingMessage } from '@app/actions/spinnerActions';
import { stateArtworkImage } from '@app/actions/artworkDataActions';

import endpoint from '@app/endpoint';

export const setAssignedArtworkId = artworkResponse => ({
  type: SET_ASSIGNED_ARTWORK_ID,
  payload: artworkResponse,
});

const prepareArtworkFormData = (artworkInfo, productId) => {
  const formData = new FormData();
  const { file, designName } = artworkInfo;
  formData.append('file', file);
  const artworkNameBlob = new Blob([designName], {
    type: 'text/plain;charset=UTF-8',
  });
  formData.append('name', artworkNameBlob);
  const fileNameBlob = new Blob([file.name], {
    type: 'text/plain;charset=UTF-8',
  });
  formData.append('filename', fileNameBlob);
  formData.append('productId', productId);
  return formData;
};

export const updateArtwork = (artworkInfo, productId, dispatch) => {
  const { artwork_id: artworkId } = artworkInfo;
  const artworkFormData = prepareArtworkFormData(artworkInfo, productId);
  dispatch(startLoading());
  endpoint.updateArtwork(artworkFormData, artworkId, response => {
    const { data: assignedArtworkId } = response || {};
    dispatch(setAssignedArtworkId(assignedArtworkId));
    dispatch(endLoading());
  });
};

export const uploadArtwork = (artworkInfo, productId, dispatch, behavior, quoteAssetId) => {
  const artworkFormData = prepareArtworkFormData(artworkInfo, productId);
  dispatch(startLoading());
  endpoint.uploadArtwork(artworkFormData, { behavior, quoteAssetId }, response => {
    const { data: assignedArtworkId } = response || {};
    dispatch(setAssignedArtworkId(assignedArtworkId));
    dispatch(endLoading());
  });
};

export const processArtworkUpload = (artworkInfo, productId, quoteAssetId, behavior) => dispatch => {
  const { artwork_id: artworkId } = artworkInfo;
  const method = artworkId ? updateArtwork : uploadArtwork;
  method.call(this, artworkInfo, productId, dispatch, behavior, quoteAssetId);
};

export const fetchArtwork = artworkId =>
  new Promise((resolve, reject) => {
    endpoint.fetchArtwork(artworkId, resolve, reject, this);
  });

export const fetchArtworkUploadResult = artworkId => dispatch =>
  new Promise(resolve => {
    endpoint.fetchArtworkStatus(
      artworkId,
      async ({ data: { artworkId, status: uploadStatus } }) => {
        let artworkData;

        if (uploadStatus === 'Done') {
          artworkData = await fetchArtwork(artworkId);
          dispatch(endLoading());
        } else {
          const validStatuses = ['ValidatingArtwork', 'StoringArtwork', 'GeneratingPreview', 'StoringPreview', 'RemovingContent'];
          const statusMessage = validStatuses.includes(uploadStatus)
            ? 'qau-status-' + uploadStatus
            : 'qau-status-processing';
          dispatch(updateLoadingMessage(statusMessage));
        }

        resolve(artworkData);
      },
      () => dispatch(endLoading()),
      this,
    );
  });

export const uploadArtworkToProject = () => (dispatch, getState) =>
  new Promise(resolve => {
    const {
      artwork: { info: artworkInfo, project: projectData },
      matrix: {
        ctx: { behavior },
      },
    } = getState();
    const { artwork_id: artworkId } = artworkInfo;
    const params = { artworkId, behavior, projectData };

    dispatch(startLoading());
    endpoint.uploadArtworkToProject(
      params,
      ({ data: imageDto }) => {
        dispatch(stateArtworkImage(imageDto));
        dispatch(endLoading());
        resolve({ artworkInfo, projectData, image: imageDto });
      },
      () => dispatch(endLoading()),
    );
  });

const uploadSingleArtwork = (fileData, { productId, predesignId, quoteAssetId }, onStatusChange, fileRequests) =>
  new Promise((resolve, reject) => {
    const { file, filename, id } = fileData;

    if (fileRequests?.get(id) === null) {
      resolve(null);
      return;
    }

    if (onStatusChange?.(id, 'loading') === false) {
      resolve(null);
      return;
    }

    const formData = new FormData();

    formData.append('file', file);
    formData.append('name', new Blob([filename], { type: 'text/plain;charset=UTF-8' }));
    formData.append('filename', new Blob([file.name], { type: 'text/plain;charset=UTF-8' }));
    formData.append('productId', productId);
    formData.append('predesignId', predesignId);
    formData.append('quoteAssetId', quoteAssetId);

    const request = endpoint.createArtwork(
      formData,
      response => {
        fileRequests?.delete(id);
        onStatusChange?.(id, 'success');
        resolve(response);
      },
      errors => {
        fileRequests?.delete(id);
        onStatusChange?.(id, 'error', errors);
        reject(errors);
      },
    );

    request.xhr.addEventListener('abort', () => {
      fileRequests?.delete(id);
      resolve(null);
    });

    fileRequests?.set(id, request);
  });

const processUploadQueue = async (queue, params, onStatusChange, fileRequests) => {
  const CONCURRENT_UPLOADS = 3;
  const results = [];
  let currentIndex = 0;

  const uploadNext = async () => {
    while (currentIndex < queue.length) {
      const index = currentIndex++;
      try {
        results[index] = await uploadSingleArtwork(queue[index], params, onStatusChange, fileRequests);
      } catch (error) {
        results[index] = error;
      }
    }
  };

  await Promise.all(Array.from({ length: Math.min(CONCURRENT_UPLOADS, queue.length) }, () => uploadNext()));

  return results;
};

export const processMultipleArtworkUpload = (files, params, onStatusChange, fileRequests) => dispatch => {
  if (!files.length) {
    return Promise.resolve([]);
  }

  return processUploadQueue(files, params, onStatusChange, fileRequests)
    .then(results => {
      return results;
    })
    .catch(error => {
      throw error;
    });
};
